import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ListItemButton, { type ListItemButtonTypeMap } from '@mui/material/ListItemButton';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepConnector from '@mui/material/StepConnector';
import MuiStepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { Check, ChevronDown, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { cn } from '@/lib/cn';
import type { NavItemLook, StepMarkLook, StepState } from '@/theme/looks';
import { Badge, type BadgeVariant } from './display';
import { ICON_SIZE } from './iconSize';

const ItemRoot = styled(ListItemButton, { name: 'KitNavItem', slot: 'root' })({}) as OverridableComponent<
  ListItemButtonTypeMap<{ ownerState: NavItemLook }>
>;
const GroupRoot = styled(ButtonBase, { name: 'KitNavGroup', slot: 'root' })({});

export interface NavItemProps {
  to: string;
  end?: boolean;
  /** Shows the icon alone, centred. */
  rail?: boolean;
  /** Sits on a tinted block, so the hover is the surface colour. */
  onSurface?: boolean;
  title?: string;
  'aria-label'?: string;
  className?: string;
  children: ReactNode;
}

/** Shows one nav link. The current page gets the active look. */
export function NavItem({ to, end, rail = false, onSurface = false, className, children, ...props }: NavItemProps) {
  return (
    <ItemRoot
      component={NavLink}
      to={to}
      end={end}
      ownerState={{ rail, onSurface }}
      className={className}
      {...props}
    >
      {children}
    </ItemRoot>
  );
}

/** Shows a group heading that opens and closes the group. */
export function NavGroupToggle({ open, onToggle, children }: { open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <GroupRoot
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full justify-between whitespace-nowrap px-3 pb-1 pt-3.5 text-left"
    >
      {children}
      <ChevronDown size={ICON_SIZE.xs} aria-hidden className={cn('transition-transform', !open && '-rotate-90')} />
    </GroupRoot>
  );
}

/** Shows the icon of a group or a label when the nav is a rail. */
export function NavRailMark({ label, icon }: { label: string; icon?: ReactNode }) {
  return (
    <Box
      component="span"
      role="img"
      aria-label={label}
      title={label}
      sx={{ color: 'text.secondary', borderTop: 1, borderColor: 'divider' }}
      className="mt-2 flex h-8 shrink-0 items-center justify-center"
    >
      {icon}
    </Box>
  );
}

/** Shows a tinted block of nav items, such as the entries of an open brand. */
export function NavInset({ compact = false, children }: { compact?: boolean; children: ReactNode }) {
  return (
    <Box
      sx={{ bgcolor: 'grey.100', border: 1, borderColor: 'divider' }}
      className={cn('mt-3 flex flex-col gap-0.5 rounded-xl p-1.5', compact && 'mx-0.5 px-0.5')}
    >
      {children}
    </Box>
  );
}

export interface NavDrawerProps {
  /** Fixed at the side. Otherwise it slides in over the page while `open`. */
  permanent: boolean;
  open: boolean;
  onClose: () => void;
  /** Narrows it to a rail of icons. */
  collapsed: boolean;
  ariaLabel: string;
  /** The row above the links: the logo and the collapse button. */
  header: ReactNode;
  children: ReactNode;
}

/** Shows the app nav in a drawer. A sliding drawer closes on Escape and on a click outside. */
export function NavDrawer({ permanent, open, onClose, collapsed, ariaLabel, header, children }: NavDrawerProps) {
  return (
    <Drawer
      variant={permanent ? 'permanent' : 'temporary'}
      open={permanent || open}
      onClose={onClose}
      slotProps={{
        root: { keepMounted: true, closeAfterTransition: false },
        paper: { className: cn('transition-[width]', collapsed ? 'w-16' : 'w-56') },
      }}
    >
      <nav aria-label={ariaLabel} className="flex h-full flex-col">
        <div
          className={cn(
            'flex min-h-16 shrink-0 items-center gap-2.5 px-4',
            collapsed && 'flex-col justify-center gap-2 px-0 py-3',
          )}
        >
          {header}
        </div>
        <Divider />
        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2">{children}</div>
      </nav>
    </Drawer>
  );
}

export interface TabItem {
  key: string;
  label: string;
  disabled?: boolean;
  /** A small mark after the label, such as a Badge. */
  badge?: ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
  ariaLabel?: string;
  /** Shares the row's width between the tabs; the row no longer scrolls. */
  fit?: boolean;
}

/** Shows MUI tabs with an underline in one row. The row scrolls if the tabs do not fit. */
export function Tabs({ tabs, activeKey, onChange, className, ariaLabel, fit = false }: TabsProps) {
  return (
    <MuiTabs
      value={activeKey}
      onChange={(_, key: string) => onChange(key)}
      aria-label={ariaLabel}
      variant={fit ? 'fullWidth' : 'scrollable'}
      scrollButtons={false}
      className={className}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          value={tab.key}
          disabled={tab.disabled}
          label={
            <span className="flex min-w-0 items-center gap-1.5">
              <span className={fit ? 'truncate' : undefined}>{tab.label}</span>
              {tab.badge}
            </span>
          }
        />
      ))}
    </MuiTabs>
  );
}

export type { StepState };

export interface StepperStep {
  key: string;
  label: string;
  state: StepState;
  /** A short word beside the label: "Optional", "Skipped". Part of the accessible name. */
  badge?: string;
  badgeVariant?: BadgeVariant;
}

export interface StepperProps {
  steps: StepperStep[];
  activeKey: string;
  onSelect: (key: string) => void;
}

export interface StepMarkProps {
  state: StepState;
  index: number;
  /** Fills the circle of the active step. A complete step keeps its check. */
  active?: boolean;
}

const Mark = styled('span', { name: 'KitStepMark', slot: 'root' })<{ ownerState: StepMarkLook }>({});

/** Shows the circle of one step: its number, a check once complete, a dash once skipped. */
export function StepMark({ state, index, active = false }: StepMarkProps) {
  const filled = active && state !== 'complete';
  const isSkipped = state === 'skipped' && !filled;
  return (
    <Mark ownerState={{ state, active: filled }}>
      {state === 'complete' && <Check size={ICON_SIZE.xs} aria-hidden />}
      {isSkipped && <Minus size={ICON_SIZE.xs} aria-hidden />}
      {state !== 'complete' && !isSkipped && index + 1}
    </Mark>
  );
}

/** Shows the steps of a wizard as an MUI stepper, in their done, active and pending states. */
export function Stepper({ steps, activeKey, onSelect }: StepperProps) {
  return (
    <MuiStepper
      nonLinear
      activeStep={steps.findIndex((step) => step.key === activeKey)}
      connector={<StepConnector className="hidden sm:block" />}
      className="overflow-x-auto pb-1"
    >
      {steps.map((step, index) => {
        const active = step.key === activeKey;
        return (
          <Step key={step.key} completed={step.state === 'complete'} disabled={step.state === 'locked'}>
            <StepButton
              icon={<StepMark state={step.state} index={index} active={active} />}
              optional={step.badge && <Badge variant={step.badgeVariant ?? 'gray'}>{step.badge}</Badge>}
              aria-current={active ? 'step' : undefined}
              aria-label={step.badge ? `${step.label}, ${step.badge}` : step.label}
              onClick={() => onSelect(step.key)}
            >
              {step.label}
            </StepButton>
          </Step>
        );
      })}
    </MuiStepper>
  );
}
